import type { PlaybookVersionNoGraph } from '@onefootprint/request-types/dashboard';
import { useRouter } from 'next/router';
import { useHasPercyChatAccess } from '@/components/agent-chat/hooks/use-chat-widget-enabled';
import useChatWidgetLayoutSwitch from '@/components/agent-chat/hooks/use-chat-widget-layout-switch';
import { useStore } from '@/components/playbook-builder';
import { INVESTIGATION_CHAT_PARAM } from '@/config/constants';
import { useStartChat } from '@/features/ask-percy';
import useOrgSession from '@/hooks/use-org-session';
import usePermissions from '@/hooks/use-permissions';
const usePlaybookAudit = (selectedVersion: Pick<PlaybookVersionNoGraph, 'id' | 'version'>) => {
  const router = useRouter();
  const chat = useStartChat();
  const openChat = useChatWidgetLayoutSwitch();
  const {
    data: org
  } = useOrgSession();
  const {
    hasPermission
  } = usePermissions();
  const hasPercyAccess = useHasPercyChatAccess();
  const {
    data: {
      settings,
      playbookId,
      obcId,
      sessionId
    }
  } = useStore();
  const canAudit = hasPercyAccess && hasPermission('manual_review') && !!org && !!playbookId && (!!sessionId || !!obcId && obcId === selectedVersion.id);
  const audit = async () => {
    if (!canAudit || !playbookId || !org || chat.isLoading) return;
    let target = `Review v${selectedVersion.version} (${obcId}).`;
    if (sessionId) {
      const draft = [obcId, `edit session ${sessionId}`].filter(Boolean).join('; ');
      target = `Review the current unsaved draft (${draft}).`;
    }
    try {
      await chat.submit({
        query: `Use the playbook-audit skill to review this playbook\n\n${target}`,
        files: [],
        isLive: org.isLive,
        context: {
          kind: 'playbook',
          id: playbookId,
          name: settings.name,
          edit_session_id: sessionId ?? null,
          version_id: sessionId ? null : obcId ?? null,
          selection: null,
          update_description: null
        },
        onSuccess: id => {
          openChat('sidebar');
          void router.replace({
            pathname: router.pathname,
            query: {
              ...router.query,
              [INVESTIGATION_CHAT_PARAM]: id
            }
          }, undefined, {
            shallow: true
          });
        }
      });
    } catch {
      // useStartChat owns error toasts; leave the editor available to retry.
    }
  };
  return {
    audit,
    canAudit,
    isAuditing: chat.isLoading
  };
};
export default usePlaybookAudit;